import { createApp } from "vue";
import i18n from "@plugins/i18n";
import AlgebraKitModal from "./AlgebraKitModal";
import DOMUtils from "@utils/dom.utils";
import richtextUtils from "../../utils/richtext.utils";
import AlgebraKit from "@plugins/richtext/plugins/algebra-kit/AlgebraKit";
import AlgebraKitContentView from "@plugins/richtext/plugins/algebra-kit/AlgebraKitContentView";
import algebraKitApi from "@/apis/algebraKit.api.ts";
import { isEmpty } from "@utils/var.check";
import ROLE from "@configs/role";
import AppInstanceUtils from "@plugins/common/service/app.instance.utils";
import { AlgebraKitEventBus } from "@plugins/richtext/service/algebrakit.event.bus";
import { noOp } from "@utils/function.utils.ts";

export default function (editor) {
  const richTextEditor = editor.options.get("richTextEditor");
  const context = richTextEditor.richTextContext || {};

  let spec = context.algebrakitSpec;
  let refId = context.algebrakitRefId;
  let showFeedback = null;
  let hasRandomised = null;
  let modalId = null;
  let legacySpec: string | null = null;

  editor.ui.registry.addButton("algebra_kit", {
    tooltip: i18n.t("tinymce_plugin_algebra_kit_button_tooltip"),
    icon: "algebrakit",
    onAction: function () {
      if (validationToCreate()) {
        openAlgebraKitModal();
      } else {
        const props = {
          title: i18n.t("algebra_kit_creation_validation_title"),
          content: i18n.t("algebra_kit_creation_validation"),
          primaryBtnLabel: i18n.t("button_ok"),
          use_secondaryButton: false,
          actionPrimary: noOp,
        };
        richTextEditor.$modal.showMessage("warning", props, true);
      }
    },
  });

  editor.addCommand("mceAlgebraKitDelete", (targetNode) => {
    richtextUtils.removeNodeFromEditor(editor, targetNode);
  });

  editor.on("click", (e) => {
    const target = e.target;
    if (target.closest("button.edit") && target.closest("button.edit").length !== 0) {
      openAlgebraKitModal();
    }

    if (target.closest("button.trash") && target.closest("button.trash").length !== 0) {
      const targetNode = target.closest(".sls-algebra-kit-wrapper");
      if (targetNode) editor.execCommand("mceAlgebraKitDelete", targetNode);
    }

    // cater for cases when have undo action
    handlePluginRerender(target);
  });

  (function init() {
    richTextEditor.plugins["algebrakit"] = {
      renderInEditor,
      cleanUp,
      containPluginElements,
      openAlgebraKitModal,
    };
  })();

  function validationToCreate() {
    const isAlgebraKitInEditor = editor.getBody().getElementsByClassName("sls-algebra-kit");
    return isAlgebraKitInEditor.length === 0;
  }

  function openAlgebraKitModal() {
    const eventBusWatchInstances = [];
    eventBusWatchInstances.push(AlgebraKitEventBus.$on("embed-algebra-kit", handleAddAlgebraKit));
    modalId = richTextEditor.$modal.show(
      AlgebraKitModal,
      {
        spec: spec,
        refId: refId,
        isScorable: context.isScorable,
        showFeedback: showFeedback,
        eventBusWatchInstances,
      },
      {},
      true,
    );
  }

  function renderInlineAlgebraKit(selector) {
    DOMUtils.elOf(editor.getBody(), selector).forEach((element) => {
      if (isEmpty(spec) && !isEmpty(element.getAttribute("data-spec"))) {
        legacySpec = element.getAttribute("data-spec");
        spec = JSON.parse(legacySpec!.replaceAll("##", "$$$"));
      }

      if (spec) {
        showFeedback = element.getAttribute("data-show-feedback") === "true";
        hasRandomised = element.getAttribute("data-has-randomised") === "true";
        const requestData = {
          exercises: [
            {
              exerciseSpec: spec,
            },
          ],
          assessmentMode: !showFeedback,
        };

        const exercise = JSON.stringify(requestData);
        let sessionId;
        algebraKitApi
          .createSession(exercise)
          .then((response) => {
            sessionId = response[0][0].sessionId;
            if (isEmpty(sessionId) || !response[0][0].success) {
              sessionId = "null";
            }
            mountAlgebraKitComponent(element, sessionId);
          })
          .catch((error) => {
            console.log(error);
            sessionId = "null";
            mountAlgebraKitComponent(element, sessionId);
          });
      } else {
        console.error("Unable to resolve algebra-kit data");
        mountAlgebraKitComponent(element, null);
      }
    });
  }

  function renderInlineAlgebraKitContent(selector) {
    DOMUtils.elOf(editor.getBody(), selector).forEach((element) => {
      if (!isEmpty(element.getAttribute("data-spec"))) {
        const specString = element.getAttribute("data-spec");
        richtextUtils.encloseElementWithWrapper(element, {
          class: "sls-algebra-kit-content-wrapper",
          contenteditable: true,
        });
        const AlgebraKitContentViewApp = createApp(AlgebraKitContentView, {
          ...AppInstanceUtils.generateDynamicMountProps(richTextEditor),
          dataSpec: specString.replaceAll("##", "$$$"),
        });
        AppInstanceUtils.installCommonPlugin(AlgebraKitContentViewApp);
        const richTextAlgebraKitContentView = AlgebraKitContentViewApp.mount(element);
        DOMUtils.replaceComponentWrapperWithComponentElement(richTextAlgebraKitContentView);
        const algebraKitWrapper = richTextAlgebraKitContentView.$el.closest(
          ".sls-algebra-kit-content-wrapper",
        );
        richtextUtils.addNewLineIfIsLastElement(algebraKitWrapper);
      } else {
        console.error("Unable to resolve algebra-kit data");
      }
    });
  }

  function mountAlgebraKitComponent(element, sessionId) {
    const isTeacher = context && context.viewingRole === ROLE.TEACHER;
    //wrap in non-editable wrapper
    richtextUtils.encloseElementWithWrapper(element, {
      class: "sls-algebra-kit-wrapper",
      contenteditable: true,
    });

    if (isEmpty(refId) && !isEmpty(spec)) {
      if (context.isScorable) {
        refId = spec.definition.elements[0].interactions[0].id;
      } else {
        refId = spec.definition.elements[0].id;
      }
    }

    const AlgebraKitApp = createApp(AlgebraKit, {
      ...AppInstanceUtils.generateDynamicMountProps(richTextEditor),
      sessionId: sessionId,
      refId: refId,
      reviewMode: isTeacher,
      showActionTools: sessionId !== null,
      solutionMode: false,
      showFeedback: showFeedback,
      hasRandomised: hasRandomised,
      isScorable: context.isScorable,
      legacySpec: legacySpec,
    });
    AppInstanceUtils.installCommonPlugin(AlgebraKitApp);
    const richTextAlgebraKit = AlgebraKitApp.mount(element);
    DOMUtils.replaceComponentWrapperWithComponentElement(richTextAlgebraKit);

    const algebraKitWrapper = richTextAlgebraKit.$el.closest(".sls-algebra-kit-wrapper");
    richtextUtils.addNewLineIfIsLastElement(algebraKitWrapper);
  }

  function renderInEditor() {
    if (DOMUtils.elOf(editor.getBody(), ".sls-algebra-kit"))
      renderInlineAlgebraKit(".sls-algebra-kit");
    if (DOMUtils.elOf(editor.getBody(), ".sls-algebra-kit-content"))
      renderInlineAlgebraKitContent(".sls-algebra-kit-content");
  }

  function cleanUp(content) {
    const containerAfterCleanUp = document.createElement("div");
    containerAfterCleanUp.innerHTML = content;

    DOMUtils.elOf(containerAfterCleanUp, ".sls-algebra-kit").forEach((element) => {
      cleanUpElement(containerAfterCleanUp, element);
    });
    DOMUtils.elOf(containerAfterCleanUp, ".sls-algebra-kit-content").forEach((element) => {
      cleanUpElement(containerAfterCleanUp, element);
    });
    return containerAfterCleanUp.innerHTML;
  }

  function cleanUpElement(container, element) {
    let wrapper = element.closest(".sls-algebra-kit-wrapper");
    if (wrapper) {
      wrapper.replaceWith(element);
    }
    wrapper = element.closest(".sls-algebra-kit-content-wrapper");
    if (wrapper) {
      wrapper.replaceWith(element);
    }
  }

  function containPluginElements(container) {
    return (
      container.querySelectorAll(".sls-algebra-kit").length > 0 ||
      container.querySelectorAll(".sls-algebra-kit-content").length > 0
    );
  }

  function handlePluginRerender(target) {
    const algebraKitComponent = target.closest(".algebra-kit-component");
    if (!algebraKitComponent || algebraKitComponent.length === 0 || algebraKitComponent.__vue__)
      return;

    const akTargetNode = algebraKitComponent.closest(".sls-algebra-kit");
    const akContentTargetNode = algebraKitComponent.closest(".sls-algebra-kit-content");
    if (
      (!akTargetNode || akTargetNode.length === 0) &&
      (!akContentTargetNode || akContentTargetNode.length === 0)
    )
      return;
    if (akTargetNode && akTargetNode.length !== 0) {
      cleanUpElement(editor.getBody(), akTargetNode);
    }
    if (akContentTargetNode && akContentTargetNode.length !== 0) {
      cleanUpElement(editor.getBody(), akContentTargetNode);
    }
    renderInEditor();
  }

  function handleAddAlgebraKit(data) {
    richTextEditor.$modal.remove(modalId);
    createInLineAlgebraKitAttachment(data);
  }

  function createInLineAlgebraKitAttachment(algebraKitSpec) {
    DOMUtils.elOf(editor.getBody(), ".sls-algebra-kit").forEach((element) => {
      const targetNode = element.closest(".sls-algebra-kit-wrapper");
      if (targetNode) editor.execCommand("mceAlgebraKitDelete", targetNode);
    });

    spec = algebraKitSpec.spec;
    showFeedback = algebraKitSpec.showHintAndFeedback;
    hasRandomised = algebraKitSpec.hasRandomised;
    refId = algebraKitSpec.refId;

    const attributes = {
      class: "sls-algebra-kit",
      "data-show-feedback": showFeedback,
      "data-has-randomised": hasRandomised,
    };

    if (!context.isQuestion) {
      attributes["data-spec"] = JSON.stringify(spec).replaceAll("$$", "##");
    }

    const placeholder = editor.dom.createHTML("div", attributes);
    editor.insertContent(placeholder);
    renderInlineAlgebraKit(".sls-algebra-kit");
    editor.save();
  }
}
